import {Component, OnInit, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {RefData} from '../../../_models/ref.data.model';
import {CrisisAlertService} from '../../services/crisis-alert.service';
import {Attribute} from '../../../_models/attribute.model';
import {Entity} from '../../../_models/entity.model';
import {Case} from '../../../_models/case.model';
import {Part} from '../../../_models/part.model';
import {User} from '../../../_models/user.model';
import {environment} from '../../../../environments/environment';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {UploadDialogComponent} from '../upload-dialog/upload-dialog.component';
import {MatStepper} from '@angular/material/stepper';
import {SpinnerDialogComponent} from '../spinner-dialog/spinner-dialog.component';
import {UploadFormValidator} from './upload.form.validator';
import {Constants} from '../../constants';
import {SessionManagementService} from '../../../core/services/session-management.service';

export interface UploadDialogData {
  type: string;
  errorMsg: string;
  caseId: string;
  candidateId: string;
  testName: string;
}

@Component({
  selector: 'app-upload',
  templateUrl: './upload.component.html',
  styleUrls: ['./upload.component.scss']
})
export class UploadComponent implements OnInit {

  isLinear = true;
  firstFormGroup: FormGroup;
  refData: RefData;
  prompts: Attribute[];
  disabledPrompts: string[];
  courseTests: Entity[];
  tags: Entity[];
  case: Case;
  part: Part;
  fileName: string;
  fileUri: string;
  fileExtension: string;
  fileData: string;
  fileMsg: string;
  attached: number;
  mediaObject: any;
  private user: User;
  private file: File;
  @ViewChild('stepper', {static: false}) private stepper: MatStepper;

  constructor(private _formBuilder: FormBuilder,
              private crisisAlertService: CrisisAlertService,
              private sessionManagementService: SessionManagementService,
              public dialog: MatDialog) {
  }

  ngOnInit() {
    this.user = JSON.parse(sessionStorage.getItem(Constants.AUTH_TOKEN));
    this.crisisAlertService.ensureRefData();
    this.crisisAlertService.refDataObs.subscribe(data => {
      this.refData = data;
    });
    this.reset();
    this.firstFormGroup = this._formBuilder.group({
      programCtrl: ['APX', Validators.required],
      testDateCtrl: [this.testDate(), [Validators.required, UploadFormValidator.dateRange]],
      testCtrl: ['', Validators.required],
      promptSetCtrl: ['ALL', Validators.required],
      specialtyCtrl: [''],
      custTrackingIdCtrl: ['', [Validators.required, Validators.pattern('^[0-9a-zA-Z\\d-_]+$'), Validators.maxLength(20)]],
      candidateInitialsCtrl: ['', [Validators.required, Validators.pattern('^[a-zA-Z]+$'), Validators.maxLength(5)]],
      flaggedDateCtrl: [new Date(), [Validators.required, UploadFormValidator.dateRange]],
      fileCtrl: ['', Validators.required]
    });
    this.firstFormGroup.get('programCtrl').valueChanges.subscribe(programCode => {
      if (programCode) {
        this.case.programCode = programCode;
        this.case.programName = this.getProgramName(programCode);
        this.courseTests = this.refData.tests.filter(x => x.auxCode === programCode);
        this.firstFormGroup.get('testCtrl').setValue('');
        this.firstFormGroup.get('specialtyCtrl').setValue('');
        /*if ('APX' === this.case.programCode) {
          this.firstFormGroup.get('promptSetCtrl').setValue('PAPER');
        } else {
          this.firstFormGroup.get('promptSetCtrl').setValue('PROMPT');
        }*/
        this.firstFormGroup.get('candidateInitialsCtrl').clearValidators();
        if ('APX' === programCode) {
          this.firstFormGroup.get('candidateInitialsCtrl').setValidators(
            [Validators.required, Validators.pattern('^[a-zA-Z]+$'), Validators.maxLength(5)]);
        } else {
          this.firstFormGroup.get('candidateInitialsCtrl').setValidators(
            [Validators.pattern('^[a-zA-Z]+$'), Validators.maxLength(5)]);
        }
        this.firstFormGroup.get('candidateInitialsCtrl').updateValueAndValidity();
      }
    });
    this.firstFormGroup.get('testCtrl').valueChanges.subscribe(testCode => {
      if (testCode) {
        this.case.testCode = testCode;
        this.case.testName = this.getTestName(testCode, this.case.programCode);
        const key = this.case.programCode + ':' + testCode;
        this.firstFormGroup.get('specialtyCtrl').setValue(this.getTagName(key));
        if ('APX' === this.case.programCode) {
          if (this.isArtAndDesign(testCode)) {
            this.firstFormGroup.get('promptSetCtrl').setValue('');
            this.enablePrompt('SLWRK');
            this.enablePrompt('SSINV');
            this.disablePrompt('ALL');
          } else {
            this.firstFormGroup.get('promptSetCtrl').setValue('ALL'); // default
            this.disablePrompt('SLWRK');
            this.disablePrompt('SSINV');
            this.enablePrompt('ALL');
          }
        } else {
          this.firstFormGroup.get('promptSetCtrl').setValue('ALL'); // default
          this.disablePrompt('SLWRK');
          this.disablePrompt('SSINV');
          this.enablePrompt('ALL');
        }
      }
    });
    this.firstFormGroup.get('specialtyCtrl').valueChanges.subscribe(specialityName => {
      if (specialityName) {
        this.case.specialityName = specialityName;
        this.case.specialityCode = this.getSpecialityCode(specialityName);
      }
    });
    this.firstFormGroup.get('flaggedDateCtrl').valueChanges.subscribe(flaggedDateValue => {
      if (flaggedDateValue) {
        this.case.flaggedDate = this.dateStringMMDDYYYY(flaggedDateValue);
      }
    });
    this.firstFormGroup.get('testDateCtrl').valueChanges.subscribe(testDateValue => {
      if (testDateValue) {
        this.case.testDate = this.dateStringMMDDYYYY(testDateValue);
      }
    });
    this.firstFormGroup.get('custTrackingIdCtrl').valueChanges.subscribe(candidateId => {
      if (candidateId) {
        this.case.candidateId = candidateId;
      }
    });
    this.firstFormGroup.get('candidateInitialsCtrl').valueChanges.subscribe(candidateInitials => {
      if (candidateInitials) {
        this.case.candidateInitials = candidateInitials;
      } else { // else part needed cause this field changes from required to not required vice versa
        this.case.candidateInitials = null;
      }
    });
    this.firstFormGroup.get('promptSetCtrl').valueChanges.subscribe(promptSetCode => {
      if (promptSetCode) {
        this.case.promptSetCode = promptSetCode;
        this.case.promptSetName = this.getPromptSetName(promptSetCode);
      }
    });
  }

  handleFile($event: any): void {
    const filePath = $event.currentTarget.value;
    this.fileName = filePath.replace(/.*[\/\\]/, '');
    console.log('filename=' + this.fileName);
    const extension = this.fileName.split('.').pop();
    if (extension) {
      const mimeCode = extension.toUpperCase();
      this.fileExtension = mimeCode;
      if (mimeCode === 'PDF'
        || mimeCode === 'TXT'
        || this.isAudio(mimeCode)
        || this.isVideo(mimeCode)
        || this.isImage(mimeCode)) {
        const fileElem: any = document.getElementById('input-file-id');
        if (fileElem.files.length > 0) {
          this.file = fileElem.files.item(0);
          if (this.file) {
            const fileKb = Math.round((this.file.size / 1024));
            // 50MB size = 52428800
            console.log('file size = ' + fileKb);
            if (this.isAudio(mimeCode) || this.isVideo(mimeCode) || mimeCode === 'PDF' || this.isImage(mimeCode)) {
              if (this.file.size < 52428800) {
                this.attached = 1;
                this.firstFormGroup.patchValue({fileCtrl: this.fileName});
                this.mediaObject = URL.createObjectURL(this.file);
              } else {
                this.attached = 2;
                this.fileMsg = 'It exceeds 50MB or file format is not supported. Please use the contacts from the Help link for further assistance.';
              }
            } else { // TXT type
              if (this.file.size < 52428800) {
                this.attached = 1;
                this.firstFormGroup.patchValue({fileCtrl: this.fileName});
                const fileReader = new FileReader();
                fileReader.readAsText(this.file);
                fileReader.onload = (_event) => {
                  const content = '' + fileReader.result;
                  this.fileData = content;
                };
              } else {
                this.attached = 2;
                // file too big
                this.fileMsg = 'It exceeds 50MB or file format is not supported. Please use the contacts from the Help link for further assistance.';
              }
            }
          } else {
            this.attached = 2;
            this.fileMsg = 'It exceeds 50MB or file format is not supported. Please use the contacts from the Help link for further assistance.';
          }
        }
      } else {
        this.attached = 2;
        // File type is not supported
        this.fileMsg = 'It exceeds 50MB or file format is not supported. Please use the contacts from the Help link for further assistance.';
      }
    } else {
      this.attached = 2;
      this.fileMsg = 'It exceeds 50MB or file format is not supported. Please use the contacts from the Help link for further assistance.';
    }
    // remove file
    if (this.attached === 2) {
      $event.currentTarget.value = '';
      this.firstFormGroup.patchValue({fileCtrl: ''});
    }
  }

  reset(): void {
    this.case = new Case();
    this.part = new Part();
    this.case.parts = new Array(this.part);
    this.case.programCode = 'APX';
    this.case.programName = 'AP';
    this.case.reviewerId = '' + this.user.userId;
    this.case.reviewerName = this.user.firstName + ' ' + this.user.lastName;
    this.attached = 0;
    this.fileMsg = 'Please try again.';
    this.courseTests = this.refData.tests.filter(x => x.auxCode === 'APX');
    this.tags = this.refData.tags;
    const testDate = this.testDate();
    this.case.testDate = this.dateStringMMDDYYYY(testDate); // initial
    const flaggedDate = new Date();
    this.case.flaggedDate = this.dateStringMMDDYYYY(flaggedDate); // initial
    this.prompts = [{name: 'All', code: 'ALL'},
      {name: 'Selected Works', code: 'SLWRK'},
      {name: 'Sustained Investigation', code: 'SSINV'}];
    this.disabledPrompts = ['SLWRK', 'SSINV'];
    if (this.firstFormGroup) { // only when rendered
      if (this.firstFormGroup.get('programCtrl')) {
        this.firstFormGroup.get('programCtrl').setValue('APX');
      }
      if (this.firstFormGroup.get('promptSetCtrl')) {
        this.firstFormGroup.get('promptSetCtrl').setValue('ALL');
        this.disablePrompt('SLWRK');
        this.disablePrompt('SSINV');
        this.enablePrompt('ALL');
      }
      if (this.firstFormGroup.get('testDateCtrl')) {
        this.firstFormGroup.get('testDateCtrl').setValue(testDate);
      }
      if (this.firstFormGroup.get('flaggedDateCtrl')) {
        this.firstFormGroup.get('flaggedDateCtrl').setValue(flaggedDate);
      }
      const fileElem: any = document.getElementById('input-file-id');
      if (fileElem) {
        fileElem.value = '';
      }
    }
  }

  getErrorMessage(value: string): string {
    if (value) {
      if ('' === value.trim()) {
        return 'Invalid value';
      } else {
        // if date check date format
        return 'Required field';
      }
    } else {
      return 'Required field';
    }
  }

  private getProgramName(programCode): string {
    return this.filterName(programCode, this.refData.programs);
  }

  private getTestName(testCode, programCode): string {
    const list = this.refData.tests;
    if (testCode && list) {
      const filteredList = list.filter(x => (x.code === testCode && x.auxCode === programCode));
      if (filteredList && filteredList.length > 0) {
        return filteredList[0].name;
      }
    }
    return '';
  }

  private getPromptSetName(promptSetCode): string {
    return this.filterName(promptSetCode, this.prompts);
  }

  private filterName(code: string, list: Attribute[]): string {
    if (code && list) {
      const filteredList = list.filter(x => x.code === code);
      if (filteredList && filteredList.length > 0) {
        return filteredList[0].name;
      }
    }
    return '';
  }

  private getTagName(key: string): string {
    const tags = this.refData.tags.filter(x => x.auxCode === key);
    if (tags && tags.length > 0) {
      return tags[0].name;
    }
    return 'General';
  }

  private getSpecialityCode(specialityName): string {
    const specialities = this.refData.specialities.filter(x => x.name === specialityName);
    if (specialities && specialities.length > 0) {
      return specialities[0].code;
    }
    return '';
  }

  private testDate(): Date {
    const year = new Date().getFullYear();
    return new Date('May 1, ' + year + ' 00:00:01');
  }

  private dateStringMMDDYYYY(date: Date): string {
    return (date.getMonth() + 1) + '/' + date.getDate() + '/' + date.getFullYear();
  }

  private dateStringYYYYMMDD(date: Date): string {
    const m = (date.getMonth() + 1);
    const d = date.getDate();
    return '' + date.getFullYear() + (m > 10 ? '' + m : '0' + m) + (d > 10 ? '' + d : '0' + d);
  }

  upload() {
    const spinDialogRef = this.dialog.open(SpinnerDialogComponent, {
      hasBackdrop: true,
      disableClose: true,
      width: '148px',
      data: {}
    });
    const s3Path = this.getS3FilePath();
    this.part.uri = environment.s3.bucket + '/public/' + s3Path + '/' + this.fileName;
    this.part.mimeCode = this.file.type;
    this.part.data = btoa(this.fileData);
    console.log(JSON.stringify(this.case));
    this.crisisAlertService.addCaseDB(this.case)
      .subscribe(_case => {
          const dialogData: UploadDialogData = {
            type: 'fail',
            errorMsg: 'Crisis case submission failed due to unknown failure. Please use the contacts from the Help link for further assistance.',
            caseId: '',
            candidateId: '',
            testName: ''
          };
          if (_case) {
            if (_case.caseId) {
              console.log('New CR_CLSTR_ID=' + _case.caseId);
              this.crisisAlertService.uploadFileToS3(this.file, s3Path).subscribe(x => {
                if ('fail' === x) {
                  dialogData.type = 'fail';
                  dialogData.errorMsg = 'The system failed to process the selected file. Please use the contacts from the Help link for further assistance.';
                } else {
                  dialogData.caseId = _case.caseId;
                  dialogData.candidateId = _case.candidateId;
                  dialogData.testName = _case.testName;
                  dialogData.type = 'pass';
                }
                spinDialogRef.close('spin');
                this.openDialog(dialogData);
              });
            } else {
              if ('D010' === _case.errorMsg) {
                dialogData.errorMsg = 'There is an existing case with the same identifier, please check again.';
              } else if ('E060' === _case.errorMsg) {
                dialogData.errorMsg = 'Crisis case submission failed due to Invalid Mime Type. Please use the contacts from the Help link for further assistance.';
              } else if ('0000' === _case.errorMsg) {
                dialogData.errorMsg = 'Crisis case submission failed due to unknown failure. Please use the contacts from the Help link for further assistance.';
              } else {
                dialogData.errorMsg = 'Crisis case submission failed due to failure ' + _case.errorMsg + '. Please use the contacts from the Help link for further assistance.';
              }
              spinDialogRef.close('spin');
              this.openDialog(dialogData);
            }
          } else {
            spinDialogRef.close('spin');
            this.openDialog(dialogData);
          }
        }
      );
  }

  private getS3FilePath() {
    const testDate = this.firstFormGroup.get('testDateCtrl').value;
    return 'data/responses/CARS/'
      + this.case.programCode + '/'
      + this.case.testCode + '/'
      + this.dateStringYYYYMMDD(testDate) + '/'
      + this.case.candidateId;
  }

  private openDialog(data: UploadDialogData): MatDialogRef<any> {
    const dialogRef = this.dialog.open(UploadDialogComponent, {
      hasBackdrop: true,
      disableClose: true,
      width: '550px',
      data: data
    });
    dialogRef.afterClosed().subscribe(result => {
      console.log('dialog closed with ' + result);
      if ('pass' === result) {
        this.stepper.previous();
        this.stepper.reset();
        this.reset();
      } else if ('fail' === result) {
        this.stepper.previous();
      } else {
        // do nothing;
      }
    });
    return dialogRef;
  }

  registerAudio($event: boolean, i: any) {
    // intentionally left blank
  }

  captureNext() {
    this.sessionManagementService.extendSession();
    let media: any;
    if (this.isVideo(this.fileExtension)) {
      media = document.getElementById('native-video');
    } else if (this.isAudio(this.fileExtension)) {
      media = document.getElementById('native-sound');
    } else if ('PDF' === this.fileExtension) {
      media = document.getElementById('native-pdf');
    } else if (this.isImage(this.fileExtension)) {
      this.fileUri = this.mediaObject;
      console.log(this.fileUri);
    }
    if (media) {
      media.src = this.mediaObject;
      media.onend = function (e) {
        URL.revokeObjectURL(this.src);
      };
    }
  }

  isVideo(mimeCode) {
    return '---' === mimeCode;
  }

  isAudio(mimeCode) {
    return 'MP3' === mimeCode
      || 'OGG' === mimeCode
      || 'WAV' === mimeCode
      || 'M4A' === mimeCode;
  }

  isImage(mimeCode) {
    return 'JPG' === mimeCode
      || 'JPEG' === mimeCode
      || 'GIF' === mimeCode
      || 'PNG' === mimeCode;
  }

  isArtAndDesign(testCode) {
    return 'SA2' === testCode
      || 'SA3' === testCode
      || 'SAD' === testCode;
  }

  isSelectedToDisable(prompt: Attribute): boolean {
    return this.disabledPrompts.includes(prompt.code);
  }

  disablePrompt(promptSetCode: string): void {
    if (!this.disabledPrompts.includes(promptSetCode)) {
      this.disabledPrompts.push(promptSetCode);
    }
  }

  enablePrompt(promptSetCode: string): void {
    if (this.disabledPrompts.includes(promptSetCode)) {
      this.disabledPrompts.splice(this.disabledPrompts.indexOf(promptSetCode), 1);
    }
  }

}
